/**
 * Interface pour les éléments qui ont un ordre
 */
export interface OrderableItem {
  id: string;
  order?: number;
}

/**
 * Calcule les nouveaux ordres pour tous les éléments après un changement d'ordre
 * @param items Liste de tous les éléments (avec leur ordre actuel)
 * @param itemId ID de l'élément dont on change l'ordre
 * @param newOrder Nouvel ordre souhaité
 * @returns Map des IDs vers leurs nouveaux ordres
 */
export function calculateNewOrders<T extends OrderableItem>(
  items: T[],
  itemId: string,
  newOrder: number
): Map<string, number> {
  // Trier les items par ordre actuel
  const sortedItems = [...items].sort((a, b) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    return orderA - orderB;
  });

  // Trouver l'élément à déplacer
  const itemIndex = sortedItems.findIndex(item => item.id === itemId);
  if (itemIndex === -1) {
    // Si l'élément n'existe pas, retourner les ordres actuels
    return new Map(items.map(item => [item.id, item.order ?? 0]));
  }

  const item = sortedItems[itemIndex];
  const oldOrder = item.order ?? 0;
  const maxOrder = sortedItems.length;

  // Clamper le nouvel ordre entre 1 et le nombre total d'éléments
  const clampedNewOrder = Math.max(1, Math.min(newOrder, maxOrder));

  // Si l'ordre n'a pas changé, ne rien faire
  if (clampedNewOrder === oldOrder) {
    return new Map(items.map(item => [item.id, item.order ?? 0]));
  }

  // Retirer l'élément de sa position actuelle
  const itemsWithoutMoved = sortedItems.filter(item => item.id !== itemId);

  // Insérer l'élément à sa nouvelle position
  const newItems = [...itemsWithoutMoved];
  newItems.splice(clampedNewOrder - 1, 0, item);

  // Réassigner les ordres de 1 à N
  const newOrders = new Map<string, number>();
  newItems.forEach((item, index) => {
    newOrders.set(item.id, index + 1);
  });

  return newOrders;
}

/**
 * Réorganise les éléments après un drag and drop (insertion entre les cartes).
 * @param items Liste de tous les éléments
 * @param draggedItemId ID de l'élément déplacé
 * @param targetItemId ID de l'élément de référence (carte survolée)
 * @param insertAfter Si true, insère après la cible ; si false, insère avant (à la place de la cible)
 * @returns Map des IDs vers leurs nouveaux ordres
 */
export function reorganizeAfterDrag<T extends OrderableItem>(
  items: T[],
  draggedItemId: string,
  targetItemId: string,
  insertAfter?: boolean
): Map<string, number> {
  const sortedItems = [...items].sort((a, b) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    return orderA - orderB;
  });

  const draggedIndex = sortedItems.findIndex(item => item.id === draggedItemId);
  const targetIndex = sortedItems.findIndex(item => item.id === targetItemId);

  if (draggedIndex === -1 || targetIndex === -1) {
    return new Map(items.map(item => [item.id, item.order ?? 0]));
  }

  const newItems = [...sortedItems];
  const [draggedItem] = newItems.splice(draggedIndex, 1);
  const targetIndexInNew = newItems.findIndex(item => item.id === targetItemId);
  if (targetIndexInNew === -1) return new Map(items.map(item => [item.id, item.order ?? 0]));

  const insertIndex = insertAfter ? targetIndexInNew + 1 : targetIndexInNew;
  newItems.splice(insertIndex, 0, draggedItem);

  const newOrders = new Map<string, number>();
  newItems.forEach((item, index) => {
    newOrders.set(item.id, index + 1);
  });
  return newOrders;
}
